/**
 * DraggableObstruction Component
 * Interactive obstruction polygon with drag, rotate, and resize capabilities
 *
 * Extracted from GeoTiffViewerEmbedded.tsx (lines 505-897, 392 lines)
 *
 * Features:
 * - Drag to move entire obstruction
 * - Drag vertices to resize
 * - Drag edges to resize (perpendicular movement)
 * - Drag rotation handle to rotate around center
 * - Alt+click to duplicate with 1m offset
 * - Double-click handler for custom actions
 * - Visual feedback with selection state
 */

import React, { useState, useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import { Polygon, Polyline, CircleMarker, Marker } from 'react-leaflet';
import L from 'leaflet';

export interface DraggableObstructionProps {
  /** Obstruction coordinates [lat, lng][] - typically 4 corners for rectangle */
  obstruction: [number, number][];

  /** Whether this obstruction is currently selected */
  isSelected: boolean;

  /** Callback when obstruction is selected */
  onSelect: () => void;

  /** Callback when obstruction is moved (drag or resize) */
  onMove: (newCoords: [number, number][]) => void;

  /** Callback when obstruction is rotated */
  onRotate: (newCoords: [number, number][]) => void;

  /** Callback for double-click (typically delete) */
  onDoubleClick: () => void;

  /** Optional: Callback for Alt+click duplication */
  onDuplicate?: (offsetCoords: [number, number][]) => void;
}

/**
 * Interactive obstruction component with full editing capabilities
 *
 * Interaction modes:
 * - Click polygon: Select
 * - Drag polygon: Move
 * - Drag blue vertex handles: Resize by moving corner
 * - Drag green edge handles: Resize by moving edge perpendicular
 * - Drag purple rotation handle: Rotate around center
 * - Alt+click polygon: Duplicate with 1m offset
 * - Double-click polygon: Trigger onDoubleClick (e.g., delete)
 */
export function DraggableObstruction({
  obstruction,
  isSelected,
  onSelect,
  onMove,
  onRotate,
  onDoubleClick,
  onDuplicate,
}: DraggableObstructionProps) {
  const map = useMap();

  // Interaction state
  const [isDragging, setIsDragging] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizingVertex, setResizingVertex] = useState<number | null>(null);
  const [isDraggingEdge, setIsDraggingEdge] = useState(false);
  const [draggingEdgeIndex, setDraggingEdgeIndex] = useState<number | null>(null);
  const [dragStart, setDragStart] = useState<[number, number] | null>(null);

  // Refs for drag tracking
  const dragOffsetRef = useRef<[number, number] | null>(null);
  const initialAngleRef = useRef<number>(0);
  const tempCoordsRef = useRef<[number, number][]>(obstruction);
  const edgeDragStartRef = useRef<[number, number] | null>(null);

  // Update temp coords when obstruction changes
  useEffect(() => {
    tempCoordsRef.current = obstruction;
  }, [obstruction]);

  // Calculate center for rotation
  const centerLat = obstruction.reduce((sum, [lat]) => sum + lat, 0) / obstruction.length;
  const centerLng = obstruction.reduce((sum, [, lng]) => sum + lng, 0) / obstruction.length;

  // Get the first corner for rotation detection
  const rotationCorner: [number, number] = obstruction[0];

  // --- Event Handlers ---

  const handleMouseDown = (e: any) => {
    if (e.originalEvent.button !== 0) return; // Only left click

    // Check if Option/Alt key is pressed for duplication
    if (e.originalEvent.altKey && onDuplicate) {
      // Create duplicate with small offset
      const offsetMeters = 1; // 1 meter offset
      const latDegPerMeter = 1 / 111000;
      const lngDegPerMeter = 1 / (111000 * Math.cos(obstruction[0][0] * Math.PI / 180));

      const duplicateCoords = obstruction.map(([lat, lng]) => [
        lat + offsetMeters * latDegPerMeter,
        lng + offsetMeters * lngDegPerMeter,
      ] as [number, number]);

      onDuplicate(duplicateCoords);

      // Don't start dragging the original, the duplicate will be created and selected
      L.DomEvent.stopPropagation(e);
      return;
    }

    // Move the obstruction
    setIsDragging(true);
    setDragStart([e.latlng.lat, e.latlng.lng]);
    dragOffsetRef.current = [e.latlng.lat, e.latlng.lng];
    onSelect();
    map.dragging.disable();
    L.DomEvent.stopPropagation(e);
  };

  // Handle rotation handle drag
  const handleRotationHandleMouseDown = (e: any) => {
    L.DomEvent.stopPropagation(e);
    setIsRotating(true);
    const angle = Math.atan2(
      rotationCorner[0] - centerLat,
      rotationCorner[1] - centerLng
    );
    initialAngleRef.current = angle;
    map.dragging.disable();
  };

  // Handle vertex dragging for resizing
  const handleVertexMouseDown = (index: number, e: any) => {
    L.DomEvent.stopPropagation(e);
    setIsResizing(true);
    setResizingVertex(index);
    map.dragging.disable();
  };

  // Handle edge dragging for resizing
  const handleEdgeMouseDown = (edgeIndex: number, e: any) => {
    L.DomEvent.stopPropagation(e);
    setIsDraggingEdge(true);
    setDraggingEdgeIndex(edgeIndex);
    edgeDragStartRef.current = [e.latlng.lat, e.latlng.lng];
    map.dragging.disable();
  };

  // --- Interaction Effects ---

  // Moving obstruction
  useEffect(() => {
    if (!isDragging || !dragStart) return;

    const handleMouseMove = (e: any) => {
      if (!dragOffsetRef.current) return;

      const [startLat, startLng] = dragOffsetRef.current;
      const deltaLat = e.latlng.lat - startLat;
      const deltaLng = e.latlng.lng - startLng;

      const movedCoords = obstruction.map(([lat, lng]) => [
        lat + deltaLat,
        lng + deltaLng,
      ] as [number, number]);

      onMove(movedCoords);
      dragOffsetRef.current = [e.latlng.lat, e.latlng.lng];
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setDragStart(null);
      dragOffsetRef.current = null;
      // Note: Don't enable dragging here - let SpacebarPanController manage it
    };

    map.on('mousemove', handleMouseMove);
    map.on('mouseup', handleMouseUp);

    return () => {
      map.off('mousemove', handleMouseMove);
      map.off('mouseup', handleMouseUp);
      // Note: Don't enable dragging here - let SpacebarPanController manage it
    };
  }, [isDragging, dragStart, map, obstruction, onMove]);

  // Rotating obstruction
  useEffect(() => {
    if (!isRotating) return;

    const handleRotationMove = (e: any) => {
      const currentAngle = Math.atan2(
        e.latlng.lat - centerLat,
        e.latlng.lng - centerLng
      );

      const deltaAngle = currentAngle - initialAngleRef.current;

      const cos = Math.cos(deltaAngle);
      const sin = Math.sin(deltaAngle);

      const rotated = obstruction.map(([lat, lng]) => {
        const deltaLat = lat - centerLat;
        const deltaLng = lng - centerLng;

        const newLat = centerLat + (deltaLat * cos - deltaLng * sin);
        const newLng = centerLng + (deltaLat * sin + deltaLng * cos);

        return [newLat, newLng] as [number, number];
      });

      onRotate(rotated);
      initialAngleRef.current = currentAngle;
    };

    const handleRotationUp = () => {
      setIsRotating(false);
      // Note: Don't enable dragging here - let SpacebarPanController manage it
    };

    map.on('mousemove', handleRotationMove);
    map.on('mouseup', handleRotationUp);

    return () => {
      map.off('mousemove', handleRotationMove);
      map.off('mouseup', handleRotationUp);
      // Note: Don't enable dragging here - let SpacebarPanController manage it
    };
  }, [isRotating, centerLat, centerLng, obstruction, onRotate, map]);

  // Resizing by dragging vertices
  useEffect(() => {
    if (!isResizing || resizingVertex === null) return;

    const handleResizeMove = (e: any) => {
      const newCoords = [...tempCoordsRef.current];
      newCoords[resizingVertex] = [e.latlng.lat, e.latlng.lng];
      tempCoordsRef.current = newCoords;
      onMove(newCoords);
    };

    const handleResizeUp = () => {
      setIsResizing(false);
      setResizingVertex(null);
      // Note: Don't enable dragging here - let SpacebarPanController manage it
    };

    map.on('mousemove', handleResizeMove);
    map.on('mouseup', handleResizeUp);

    return () => {
      map.off('mousemove', handleResizeMove);
      map.off('mouseup', handleResizeUp);
      // Note: Don't enable dragging here - let SpacebarPanController manage it
    };
  }, [isResizing, resizingVertex, map, onMove]);

  // Resizing by dragging edges
  useEffect(() => {
    if (!isDraggingEdge || draggingEdgeIndex === null || !edgeDragStartRef.current) return;

    const handleEdgeDragMove = (e: any) => {
      if (!edgeDragStartRef.current) return;

      const deltaLat = e.latlng.lat - edgeDragStartRef.current[0];
      const deltaLng = e.latlng.lng - edgeDragStartRef.current[1];

      // Get the two vertices that define this edge
      const coords = tempCoordsRef.current;
      const numVertices = coords.length;
      const v1Index = draggingEdgeIndex;
      const v2Index = (draggingEdgeIndex + 1) % numVertices;

      // Calculate edge direction vector
      const edgeLat = coords[v2Index][0] - coords[v1Index][0];
      const edgeLng = coords[v2Index][1] - coords[v1Index][1];
      const edgeLength = Math.sqrt(edgeLat * edgeLat + edgeLng * edgeLng);

      // Normalize edge direction
      const edgeDirLat = edgeLat / edgeLength;
      const edgeDirLng = edgeLng / edgeLength;

      // Project the mouse movement onto the perpendicular direction
      // Perpendicular to (x, y) is (-y, x)
      const perpLat = -edgeDirLng;
      const perpLng = edgeDirLat;

      // Calculate how much to move along perpendicular
      const perpMovement = deltaLat * perpLat + deltaLng * perpLng;

      // Move both vertices of this edge
      const newCoords = [...coords];
      newCoords[v1Index] = [
        coords[v1Index][0] + perpMovement * perpLat,
        coords[v1Index][1] + perpMovement * perpLng,
      ];
      newCoords[v2Index] = [
        coords[v2Index][0] + perpMovement * perpLat,
        coords[v2Index][1] + perpMovement * perpLng,
      ];

      tempCoordsRef.current = newCoords;
      onMove(newCoords);
      edgeDragStartRef.current = [e.latlng.lat, e.latlng.lng];
    };

    const handleEdgeDragUp = () => {
      setIsDraggingEdge(false);
      setDraggingEdgeIndex(null);
      edgeDragStartRef.current = null;
      // Note: Don't enable dragging here - let SpacebarPanController manage it
    };

    map.on('mousemove', handleEdgeDragMove);
    map.on('mouseup', handleEdgeDragUp);

    return () => {
      map.off('mousemove', handleEdgeDragMove);
      map.off('mouseup', handleEdgeDragUp);
      // Note: Don't enable dragging here - let SpacebarPanController manage it
    };
  }, [isDraggingEdge, draggingEdgeIndex, map, onMove]);

  // Calculate rotation handle position (outside the obstruction, offset from first corner)
  const rotationHandleLat = rotationCorner[0] + (rotationCorner[0] - centerLat) * 0.3;
  const rotationHandleLng = rotationCorner[1] + (rotationCorner[1] - centerLng) * 0.3;

  return (
    <>
      {/* Main polygon with dotted border and translucent fill */}
      <Polygon
        positions={obstruction}
        pathOptions={{
          color: isSelected ? '#2563eb' : '#6b7280',
          fillColor: isSelected ? '#93c5fd' : '#ffffff',
          fillOpacity: isSelected ? 0.5 : 0.45,
          weight: isSelected ? 3 : 2,
          dashArray: isSelected ? undefined : '5, 5',
        }}
        // @ts-ignore - react-leaflet v5 supports pane but v2 types don't
        pane="obstructionPane" // Render obstructions at z-index 450 (above polygons at 350)
        eventHandlers={{
          mousedown: handleMouseDown,
          click: (e: any) => {
            L.DomEvent.stopPropagation(e);
            onSelect();
          },
          dblclick: (e: any) => {
            L.DomEvent.stopPropagation(e);
            onDoubleClick();
          },
        }}
      />

      {/* Selection indicator line from center to first corner */}
      {isSelected && (
        <Polyline
          positions={[
            [centerLat, centerLng],
            rotationCorner,
            [rotationHandleLat, rotationHandleLng],
          ]}
          pathOptions={{
            color: '#8b5cf6',
            weight: 2,
            dashArray: '3, 3',
          }}
        />
      )}

      {/* Rotation handle - large purple circle outside obstruction */}
      {isSelected && (
        <CircleMarker
          center={[rotationHandleLat, rotationHandleLng]}
          // @ts-ignore - react-leaflet v5 supports radius but v2 types don't
          radius={8}
          pathOptions={{
            color: '#8b5cf6',
            fillColor: '#a78bfa',
            fillOpacity: 0.9,
            weight: 3,
          }}
          eventHandlers={{
            mousedown: handleRotationHandleMouseDown,
          }}
        />
      )}

      {/* Vertex handles for resizing - show when selected */}
      {isSelected && obstruction.map((coord, index) => {
        return (
          <CircleMarker
            key={`vertex-${index}`}
            center={coord}
            // @ts-ignore
            radius={4}
            pathOptions={{
              color: '#3b82f6',
              fillColor: '#3b82f6',
              fillOpacity: 1,
              weight: 2,
            }}
            eventHandlers={{
              mousedown: (e: any) => handleVertexMouseDown(index, e),
            }}
          />
        );
      })}

      {/* Edge handles for resizing - show when selected */}
      {isSelected && obstruction.map((coord, index) => {
        // Calculate next vertex (wraps around to 0 for last edge)
        const nextIndex = (index + 1) % obstruction.length;
        const nextCoord = obstruction[nextIndex];

        // Calculate edge midpoint
        const midLat = (coord[0] + nextCoord[0]) / 2;
        const midLng = (coord[1] + nextCoord[1]) / 2;

        // Use Marker with divIcon for square shape
        const squareIcon = L.divIcon({
          className: 'edge-handle',
          html: '<div style="width: 8px; height: 8px; background-color: #10b981; border: 2px solid #059669; cursor: move;"></div>',
          iconSize: [8, 8],
          iconAnchor: [4, 4],
        });

        return (
          <Marker
            key={`edge-${index}`}
            position={[midLat, midLng]}
            // @ts-ignore - react-leaflet v5 supports icon but v2 types don't
            icon={squareIcon}
            eventHandlers={{
              mousedown: (e: any) => handleEdgeMouseDown(index, e),
            }}
          />
        );
      })}
    </>
  );
}
